import React from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';

const TermsConditions = () => {
  return (
    <>
    <SEO 
      title="Terms & Conditions"
      description="Read the Terms and Conditions for shopping at House of Deepak. Understand our policies, terms of use, and guidelines for using our website and services."
      keywords="terms and conditions, terms of service, policies, user agreement, House of Deepak terms"
    />
    <div className="min-h-screen bg-cream pt-20 pb-16">
      <div className="section-padding">
        <div className="container-max">
          <motion.div
            className="max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Header */}
            <div className="text-center mb-16">
              <h1 className="text-4xl md:text-6xl font-serif font-light text-deep-brown mb-6">
                Terms & Conditions
              </h1>
              <p className="text-lg text-warm-gray max-w-2xl mx-auto leading-relaxed">
                Last updated: September 30, 2025
              </p>
            </div>

            {/* Content */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="p-8 md:p-12 space-y-8">
                
                {/* Introduction */}
                <section>
                  <h2 className="text-2xl font-serif font-light text-deep-brown mb-4">
                    Welcome to House of Deepak
                  </h2>
                  <p className="text-warm-gray leading-relaxed">
                    These Terms and Conditions ("Terms") govern your use of the House of Deepak website and your purchase of products from us. By accessing our website or making a purchase, you agree to be bound by these Terms.
                  </p>
                </section>

                {/* Acceptance of Terms */}
                <section>
                  <h3 className="text-xl font-serif text-deep-brown mb-3">
                    1. Acceptance of Terms
                  </h3>
                  <p className="text-warm-gray leading-relaxed mb-3">
                    By accessing and using our website, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
                  </p>
                </section>

                {/* Products and Services */}
                <section>
                  <h3 className="text-xl font-serif text-deep-brown mb-3">
                    2. Products and Services
                  </h3>
                  <ul className="text-warm-gray leading-relaxed space-y-2 list-disc list-inside">
                    <li>We offer traditional Indian wear under "House of Deepak" and contemporary western wear under "Scan by Deepak Raj"</li>
                    <li>All product descriptions, images, and specifications are for illustrative purposes only</li>
                    <li>We reserve the right to modify or discontinue any product without prior notice</li>
                    <li>Product availability is subject to stock and may vary</li>
                  </ul>
                </section>

                {/* Ordering and Payment */}
                <section>
                  <h3 className="text-xl font-serif text-deep-brown mb-3">
                    3. Ordering and Payment
                  </h3>
                  <ul className="text-warm-gray leading-relaxed space-y-2 list-disc list-inside">
                    <li>All orders are subject to acceptance by House of Deepak</li>
                    <li>We accept payments through secure payment gateways including credit cards, debit cards, and digital wallets</li>
                    <li>Cash on Delivery (COD) is available for select locations</li>
                    <li>Prices are subject to change without notice</li>
                    <li>We reserve the right to cancel orders for any reason</li>
                  </ul>
                </section>

                {/* User Accounts */}
                <section>
                  <h3 className="text-xl font-serif text-deep-brown mb-3">
                    4. User Accounts
                  </h3>
                  <ul className="text-warm-gray leading-relaxed space-y-2 list-disc list-inside">
                    <li>You are responsible for maintaining the confidentiality of your account information</li>
                    <li>You must provide accurate and complete information when creating an account</li>
                    <li>You are responsible for all activities that occur under your account</li>
                    <li>We reserve the right to suspend or terminate accounts that violate these terms</li>
                  </ul>
                </section>

                {/* Shipping and Delivery */}
                <section>
                  <h3 className="text-xl font-serif text-deep-brown mb-3">
                    5. Shipping and Delivery
                  </h3>
                  <ul className="text-warm-gray leading-relaxed space-y-2 list-disc list-inside">
                    <li>Delivery times are estimates and may vary based on location and product availability</li>
                    <li>Risk of loss transfers to you upon delivery to the shipping address</li>
                    <li>We are not responsible for delays caused by shipping carriers or external factors</li>
                    <li>Additional shipping charges may apply for remote locations</li>
                  </ul>
                </section>

                {/* Exchanges Only (No Returns) */}
                <section>
                  <h3 className="text-xl font-serif text-deep-brown mb-3">
                    6. Exchanges Only (No Returns)
                  </h3>
                  <ul className="text-warm-gray leading-relaxed space-y-2 list-disc list-inside">
                    <li>We do not offer returns or refunds under any circumstances</li>
                    <li>Exchanges only within <strong>2 days (48 hours)</strong> of delivery, subject to eligibility</li>
                    <li>Items must be <strong>unused, unwashed, and in original condition</strong> with tags and packaging intact</li>
                    <li>Sale/discount/clearance items are <strong>not eligible</strong> for exchange</li>
                    <li>Customers are responsible for <strong>two-way shipping charges</strong> for exchanges</li>
                    <li>Exchange will be processed after inspection; items not meeting conditions will be returned without exchange</li>
                  </ul>
                </section>

                {/* Intellectual Property */}
                <section>
                  <h3 className="text-xl font-serif text-deep-brown mb-3">
                    7. Intellectual Property
                  </h3>
                  <p className="text-warm-gray leading-relaxed">
                    All content on this website, including designs, logos, images, and text, is the property of House of Deepak and is protected by copyright and intellectual property laws. You may not reproduce, distribute, or create derivative works without our written permission.
                  </p>
                </section>

                {/* Privacy */}
                <section>
                  <h3 className="text-xl font-serif text-deep-brown mb-3">
                    8. Privacy
                  </h3>
                  <p className="text-warm-gray leading-relaxed">
                    Your privacy is important to us. Please review our Privacy Policy, which also governs your use of our website, to understand our practices regarding the collection and use of your personal information.
                  </p>
                </section>

                {/* Limitation of Liability */}
                <section>
                  <h3 className="text-xl font-serif text-deep-brown mb-3">
                    9. Limitation of Liability
                  </h3>
                  <p className="text-warm-gray leading-relaxed">
                    House of Deepak shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of our website or products, even if we have been advised of the possibility of such damages.
                  </p>
                </section>

                {/* Governing Law */}
                <section>
                  <h3 className="text-xl font-serif text-deep-brown mb-3">
                    10. Governing Law
                  </h3>
                  <p className="text-warm-gray leading-relaxed">
                    These Terms shall be governed by and construed in accordance with the laws of India. Any disputes arising from these Terms shall be subject to the exclusive jurisdiction of the courts in India.
                  </p>
                </section>

                {/* Changes to Terms */}
                <section>
                  <h3 className="text-xl font-serif text-deep-brown mb-3">
                    11. Changes to Terms
                  </h3>
                  <p className="text-warm-gray leading-relaxed">
                    We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting on our website. Your continued use of our website following any changes constitutes acceptance of the new Terms.
                  </p>
                </section>

                {/* Contact Information */}
                <section className="bg-cream p-6 rounded-lg">
                  <h3 className="text-xl font-serif text-deep-brown mb-3">
                    Contact Us
                  </h3>
                  <p className="text-warm-gray leading-relaxed">
                    If you have any questions about these Terms & Conditions, please contact us at:
                  </p>
                  <div className="mt-3 text-warm-gray">
                    <p>Email: info@houseofdeepak.in</p>
                    <p>Phone: +91 9167602861</p>
                    <p>Address: I-333, Saurabh Vihar, Badarpur-110044</p>
                  </div>
                </section>

              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
    </>
  );
};

export default TermsConditions;